import * as React from 'react'

import { Box, InputCheckBox, Text } from '@factorialco/deprecated-design-system'
import { FetchAllowancesForIncidenceDocument } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import FormRow from 'modules/core/components/FormRow'
import LabeledFancyRadioGroup from 'modules/core/components/Inputs/LabeledFancyRadioGroup'
import Select from 'modules/core/components/Inputs/Select'
import TextArea from 'modules/core/components/Inputs/TextArea'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import AllowanceSelector from 'modules/timeoff/components/TimeoffAllowances/AllowanceSelector'
import type AllowanceIncidenceForm from 'modules/timeoff/lib/AllowanceIncidence'
import cycles from 'modules/timeoff/store/collections/cycles'

import Form from './Form'

interface Props {
  form: AllowanceIncidenceForm
  policyId: number
  hasManualAdjustmentOptions: boolean
}

const ModalContent = ({ form, policyId, hasManualAdjustmentOptions }: Props) => {
  const { data } = useQuery(FetchAllowancesForIncidenceDocument, {
    variables: {
      policyId: policyId,
    },
    fetchPolicy: 'network-only',
  })
  const allowances = data?.timeoff.allowancesConnection.nodes || []
  const cycleOptions = cycles.map((cycle) => {
    return {
      value: cycle.attributes.start_at,
      label: cycle.rangeLabel,
    }
  })
  const allowanceId = form.get('timeoff_allowance_id').value
  const allowance = allowances.find((p) => p.id == allowanceId)

  const TimeTypeOptions = [
    {
      id: 0,
      value: '1',
      label: i18n.t('time_off.add_time'),
    },
    {
      id: 1,
      value: '-1',
      label: i18n.t('time_off.subtract_time'),
    },
  ]
  const isHourly = allowance?.allowanceType === 'hours'
  const AdjustFromOptions = isHourly
    ? [
        {
          value: 'available',
          label: i18n.t('time_off.incidences.available_hours'),
        },
        {
          value: 'accrued',
          label: i18n.t('time_off.incidences.accrued_hours'),
        },
      ]
    : [
        {
          value: 'available',
          label: i18n.t('time_off.incidences.available_days'),
        },
        {
          value: 'accrued',
          label: i18n.t('time_off.incidences.accrued_days'),
        },
      ]

  return (
    <>
      <FormRow>
        <AllowanceSelector field={form.get('timeoff_allowance_id')} allowances={allowances} />
      </FormRow>
      <FormRow extra={{ 'data-intercom-target': 'timeoff-incidences-adjusted-cycle-input' }}>
        <Select
          label={i18n.t('time_off.incidences.adjusted_cycle')}
          options={cycleOptions}
          field={form.get('effective_on')}
          readonly={!cycleOptions.length}
          showOriginalLabelText
          defaultOption={
            form.get('effective_on').value
              ? null
              : {
                  value: '',
                  label: i18n.t('common.select'),
                }
          }
        />
      </FormRow>
      <Form form={form} />
      <FormRow extra={{ 'data-intercom-target': 'timeoff-incidences-add-subtract-time-input' }}>
        <Box
          className="employee_profile_timeoff_tab_add_adjustment_substract_time_button"
          height="full"
        >
          <LabeledFancyRadioGroup field={form.get('sign')} options={TimeTypeOptions} />
        </Box>
      </FormRow>
      <FormRow extra={{ 'data-intercom-target': 'timeoff-incidences-adjust-from-input' }}>
        {hasManualAdjustmentOptions ? (
          <Select
            label={i18n.t('time_off.incidences.adjust_from')}
            options={AdjustFromOptions}
            field={form.get('target_balance')}
            mandatory
          />
        ) : (
          ''
        )}
      </FormRow>
      <FormRow extra={{ 'data-intercom-target': 'timeoff-incidences-description-input' }}>
        <TextArea
          label={i18n.t('time_off.incidences.field_description')}
          field={form.get('description')}
        />
      </FormRow>
      <FormRow>
        <Box flexDirection="row" gap="s8" alignItems="center">
          <InputCheckBox
            checked={form.get('_skip_notifications').value}
            onClick={() =>
              form.setValues({ _skip_notifications: !form.get('_skip_notifications').value })
            }
          />
          <Text.Normal>{i18n.t('time_off.incidences.skip_notifications_single')}</Text.Normal>
        </Box>
      </FormRow>
    </>
  )
}

export default observer(ModalContent)
